import * as z from "zod";
import {
  BinPrioritySchema,
  PersistentStorageLocationTypeSchema,
  StorageLocationType,
} from "./storage-location";
import {
  CoordinateSchema,
  RackCellSchema,
  WallSchema,
  WarehouseCellSchema,
} from "./warehouse-cell";

const BinCoordinateSchema = z.object({
  isSellable: z.boolean().nullish(),
  x: z.number(),
  y: z.number(),
  type: PersistentStorageLocationTypeSchema.nullish(),
  priority: BinPrioritySchema.nullish(),
});
export type BinCoordinate = z.infer<typeof BinCoordinateSchema>;

/** Returns true if the storage location is pickable.
 * The bin must be mapped (present in binCoordinates). If mapped but has no type, it's pickable.
 * Only BIN type is explicitly pickable; all other types are not. */
export function isPickable(
  binCoordinate: BinCoordinate | null | undefined,
): boolean {
  if (binCoordinate == null) {
    return false;
  }
  return (
    binCoordinate.type == null || binCoordinate.type === StorageLocationType.BIN
  );
}

/**
 * Axis-aligned, cell-snapped rectangle. The unit is grid cells (not pixels) so
 * zone hit-tests collapse to integer arithmetic and resolve identically on
 * client and server.
 */
export const ZoneRectangleSchema = z.object({
  // `x` / `y` can be negative — the warehouse canvas origin sits in the middle
  // of the viewport, so most drawn rectangles will have negative coordinates.
  // The save-time grid translation will normalize them into the persisted
  // grid's frame in a follow-up.
  x: z.number().int(),
  y: z.number().int(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type ZoneRectangle = z.infer<typeof ZoneRectangleSchema>;

/**
 * A logical region of the warehouse used for zone-based picking.
 *
 * Zones are identified by their key in `WarehouseGrid.zones` (a stable id —
 * generate at create time, never reuse). `name` and `color` are display-only.
 *
 * Spatial extent is a list of axis-aligned cell rectangles. Multiple
 * rectangles per zone are allowed (so an L-shape is two rectangles). Zones
 * MUST NOT overlap — the builder enforces this at draw time by clipping the
 * older zone's rectangles when a new zone is drawn over them, so the invariant
 * is structural, not validated.
 *
 * The "default zone" — exactly one per layout once zones are introduced — has
 * an empty `rectangles` array and implicitly covers every cell not claimed by
 * an explicit zone. Marking it with `isDefault: true` lets the builder pin it
 * and prevent deletion.
 */
export const ZoneSchema = z.object({
  name: z.string().min(1),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  rectangles: z.array(ZoneRectangleSchema),
  isDefault: z.boolean().optional(),
});
export type Zone = z.infer<typeof ZoneSchema>;

export const WarehouseGridSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  grid: z.array(z.array(WarehouseCellSchema)),
  racks: z.record(z.string(), RackCellSchema),
  walls: z.array(WallSchema),
  binCoordinates: z.record(z.string(), BinCoordinateSchema),
  startVertex: CoordinateSchema.nullish(),
  /**
   * Zone definitions, keyed by stable zone id. Optional for backward compat:
   * a layout without `zones` is treated by the engine as a single implicit
   * default zone covering the whole warehouse.
   */
  zones: z.record(z.string(), ZoneSchema).nullish(),
  /**
   * Ordered list of zone ids that drives the routing chain for hierarchical
   * zone picking — the tote is picked at the first zone, passed to the next,
   * and so on. Must be a permutation of `zones` keys when both are present.
   * The default zone is included like any other; conventionally pinned last
   * so bins outside any explicit zone are picked at the end of the wave.
   */
  zoneSequence: z.array(z.string()).nullish(),
});
export type WarehouseGrid = z.infer<typeof WarehouseGridSchema>;
export interface IWarehouseGridSchema extends z.ZodType<WarehouseGrid> {}
